import { useState, useEffect } from 'react';
import calendarSvg from '@/assets/calendar-check.svg';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import { callFirebaseFunction } from '@/bridge/callFirebaseFunction';

// Define types for our API calls
type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    subscriptionsEnabled?: boolean;
    subscribedDollars?: number;
    // other fields that might be returned
  };
};

type UpdateUserPrivateInfoRequest = {
  updates: Partial<{
    subscriptionsEnabled?: boolean;
    subscribedDollars?: number; // Made optional with ? mark
    // other fields from UserPrivateInfo
  }>;
};

type UpdateUserPrivateInfoResponse = {
  success: boolean;
};

export function SubscriptionSettings() {
    const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);
    const [initialSubscriptionsEnabled, setInitialSubscriptionsEnabled] = useState(false);
    
    const [userData, isLoading, fetchError] = useFirebaseFunction<
      GetUserPrivateInfoRequest, 
      GetUserPrivateInfoResponse
    >(
      'getUserPrivateInfo',
      (fn) => fn({ fields: ["subscriptionsEnabled", "subscribedDollars"] }), // Fixed string array syntax - removed comma inside string and added quotes for each field
      []
    );
    
    // Set initial values when data is loaded
    useEffect(() => {
        if (userData && !isLoading) {
            const fetchedSubscriptionsEnabled = userData.userInfo.subscriptionsEnabled ?? false;
            
            setSubscriptionsEnabled(fetchedSubscriptionsEnabled);
            setInitialSubscriptionsEnabled(fetchedSubscriptionsEnabled);
        }
    }, [userData, isLoading]);
    
    // Show error toast if fetch fails
    useEffect(() => {
        if (fetchError) {
            // toast({
            //     title: "Error",
            //     description: "Failed to load notification settings. Please try again.",
            //     variant: "destructive"
            // });
        }
    }, [fetchError]);
    
    // Handle subscription toggle
    const handleSubscriptionsToggle = async () => {
        const newValue = !subscriptionsEnabled;
        
        // Optimistically update UI
        setSubscriptionsEnabled(newValue);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { 
                updates: { 
                  subscriptionsEnabled: newValue 
                } 
              }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialSubscriptionsEnabled(newValue);
                // toast({
                //     title: "Success",
                //     description: `Push notifications ${newValue ? 'enabled' : 'disabled'}.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setSubscriptionsEnabled(initialSubscriptionsEnabled);
            console.error("Error updating subscription settings:", error); // Updated error message to match functionality
            // toast({
            //     title: "Error",
            //     description: "Failed to update subscription settings.", // Updated error message
            //     variant: "destructive"
            // });
        }
    };
    
    return (
        <div className="mb-8">
            <h2 className="text-2xl font-bold text-blue-dark mb-6">Subscription settings</h2>
            
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-blue border-t-transparent rounded-full" />
                </div>
            ) : (
                <>
                    <div className="border border-slate-200 rounded p-4 mb-3">
                        <div className='flex items-center justify-between'>
                            <div className="flex items-center">
                                <img src={calendarSvg} className='h-6 mr-3' alt='subscription'/>
                                <div>
                                    <div className="font-medium text-blue-dark">Weekly Subscription</div>
                                    <div className="text-xs text-slate">Buy cards + get free entries</div>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    className="sr-only peer"
                                    checked={subscriptionsEnabled}
                                    onChange={handleSubscriptionsToggle}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400" />
                            </label>
                        </div>

                        { subscriptionsEnabled &&
                            <div className='bg-green-300 h-6 mt-3 rounded-lg flex items-center pl-3 py-4 text-sm font-bold text-white
                                btn-bounce-subtle'>
                                Receiving {(userData?.userInfo?.subscribedDollars ?? 0) / 15} free Power Win entries
                            </div>
                        }

                        {fetchError && !fetchError?.message.includes('found') && (
                            <p className="text-red-500 text-sm mt-2.5 font-medium">{fetchError.message}</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}